import { type components, unwrap } from '@shared/api-client/index';
import { toastApiError } from '@shared/ui/toast/toast-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../core/api';
import { type Scope, scopesKey } from './scopes.hooks';

/** One turn of the intake transcript. The client resends the whole thread each
 *  turn; the endpoint now persists it on the scope (intakeMessages) so it can be
 *  revisited, alongside the brief it distils. */
export type IntakeMessage = components['schemas']['IntakeMessageRequest'];
export type IntakeResult = components['schemas']['IntakeResponse'];
/**
 * A transcript turn as the chat holds it locally. It is an {@link IntakeMessage} plus a
 * stable client `id` (so the thread keys on identity, not array index) and a client-only
 * `failed` flag for a user turn whose send never reached the server — it renders "Not
 * sent · Retry". Both `id` and `failed` are stripped back to `{ role, content }` before
 * any request or persistence.
 */
export type ChatMessage = IntakeMessage & { id?: string; failed?: boolean };

let messageSeq = 0;
/** A stable, session-unique id for a chat turn, so the thread keys on identity. */
export function nextMessageId(): string {
	messageSeq += 1;
	return `m${messageSeq}`;
}
/** The structured brief the panel edits (whole-object replace on PATCH). */
export type BriefFields = components['schemas']['BriefFieldsRequest'];

export const scopeKey = (id: string) => ['scope', id] as const;

/**
 * A single scope with its brief (PLAN-13): the intake screen reads title, shape
 * and briefFields off this. Mirrors {@link useScopes} — a failed read toasts the
 * ambient nudge while the page still renders its own loading/error off the query.
 */
export function useScope(id: string) {
	const query = useQuery<Scope>({
		queryKey: scopeKey(id),
		queryFn: async () => unwrap(await api.GET('/v1/scopes/{id}', { params: { path: { id } } })),
		// Scope paints from a persisted cache and then makes routing decisions off the scope
		// (Questions bounces on no sections, ?resume picks a stage), so a stale copy sends the
		// user to the wrong stage. Always revalidate on mount — scoped to scope queries so the
		// shared provider's default (Tasks/Help) is untouched (PLAN-88).
		refetchOnMount: 'always',
	});

	// No error toast here: a failed scope read surfaces in the stage shell's error state
	// ("This scope doesn't exist or was deleted"), so a toast would duplicate it (PLAN-92).
	return query;
}

/**
 * One turn of hosted intake (PLAN-8/9/10). Posts the FULL client-held transcript
 * and, on reply, merges the freshly-persisted brief + shape into the cached scope
 * so the brief panel fills live without a refetch. The list is invalidated too —
 * a first answer can advance the scope's status/stage.
 *
 * Failure is handled by the caller (PLAN-117): it marks the turn "not sent", keeps
 * the text in the composer, and raises one human, de-duped toast — so there's no
 * `onError` here that would surface the raw "Validation failed" string and stack a
 * toast per failed attempt.
 */
export function useIntake(id: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (messages: IntakeMessage[]): Promise<IntakeResult> =>
			unwrap(
				await api.POST('/v1/scopes/{id}/intake', {
					params: { path: { id } },
					body: { messages },
				}),
			),
		onSuccess: (result) => {
			// Fill the panel instantly from the reply, and follow the name the AI just set
			// (PLAN-128) so the band h1 + list row update live without waiting on the refetch.
			queryClient.setQueryData<Scope>(scopeKey(id), (prev) =>
				prev
					? {
							...prev,
							briefFields: result.brief,
							shape: result.shape,
							title: result.title,
							titleSource: result.titleSource,
						}
					: prev,
			);
			// …then refetch the scope: every intake turn bumps its version, and a
			// hand-edit PATCHes with expectedVersion, so a stale cached version would
			// 409 the first brief edit. The refetch reconciles version + server truth.
			queryClient.invalidateQueries({ queryKey: scopeKey(id) });
			queryClient.invalidateQueries({ queryKey: scopesKey });
		},
	});
}

/**
 * Hand-edit the brief (PLAN-14). `briefFields` is a whole-object replace, so the
 * caller sends the merged object. Writes the returned scope straight into the
 * cache (brief + version) and refreshes the list.
 */
export function useUpdateBrief(id: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (patch: {
			title?: string;
			briefFields?: BriefFields;
			shape?: NonNullable<Scope['shape']>;
			expectedVersion?: number;
		}): Promise<Scope> =>
			unwrap(await api.PATCH('/v1/scopes/{id}', { params: { path: { id } }, body: patch })),
		onError: (error) => {
			// A 409 means our cached version was stale; refetch so the next edit
			// carries the fresh version instead of wedging on repeated conflicts.
			queryClient.invalidateQueries({ queryKey: scopeKey(id) });
			toastApiError(error);
		},
		onSuccess: (updated) => {
			queryClient.setQueryData<Scope>(scopeKey(id), updated);
			queryClient.invalidateQueries({ queryKey: scopesKey });
		},
	});
}
